import { css } from "@emotion/css";
import { $, type Properties, resolve } from "signlets";

import logo from "../logo.svg";
import {
  groundBearing,
  groundDistance,
  move,
  toDegrees,
  toRadians,
} from "../math";
import { type Drop, type Position } from "../model";
import { positionEmpty } from "../position";
import { AltitudeInput } from "./AltitudeInput";
import { Button } from "./Button";
import { CheckboxInput } from "./CheckboxInput";
import { DirectionInput } from "./DirectionInput";
import { Field } from "./Field";
import { H1 } from "./H1";
import { CloseIcon } from "./icons/CloseIcon";
import { DownloadIcon } from "./icons/DownloadIcon";
import { PlayIcon } from "./icons/PlayIcon";
import { UploadIcon } from "./icons/UploadIcon";
import { LoadingButton } from "./LoadingButton";
import { Overlay } from "./Overlay";
import { PointInput } from "./PointInput";

const style = css`
  position: absolute;
  top: 1rem;
  right: 1rem;

  > .content {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    > .top {
      display: flex;
      justify-content: space-between;

      > .logo {
        height: 2rem;
        align-self: start;
      }
    }

    > h1 {
      margin: 0;
      font-size: 1.75rem;
    }

    > .grid {
      display: grid;
      gap: 1rem;
      grid-template-columns: 11rem 11rem;

      > button:last-of-type {
        grid-column: span 2;
      }
    }
  }
`;

export type SidebarProps = {
  visible: boolean;
  drop: Drop;
  onChangeDrop: (_: Partial<Drop>) => void;
  onStart: () => void;
  onEnd: () => void;
  onDrop: () => void;
  onDone: () => void;
  onSimulate: () => Promise<void>;
  onLoad: () => void;
  onSave: () => void;
  onPreflight: (_?: { simulation?: boolean }) => Promise<void>;
  onWeightBalance: () => void;
  onClose: () => void;
};

export const Sidebar = ({
  visible,
  drop,
  onChangeDrop,
  onStart,
  onEnd,
  onDrop,
  onDone,
  onSimulate,
  onLoad,
  onSave,
  onPreflight,
  onWeightBalance,
  onClose,
}: Properties<SidebarProps>) => {
  const incomplete = $(
    () =>
      positionEmpty(resolve(drop).start) ||
      positionEmpty(resolve(drop).end) ||
      positionEmpty(resolve(drop).drop),
  );

  const onChangeDropAltitude = (dropAltitude: number) =>
    onChangeDrop({ dropAltitude });

  const onChangeStart = (start: Position) => {
    onChangeDrop({ start });
    if (positionEmpty(start)) onStart();
  };

  const onChangeEnd = (end: Position) => {
    onChangeDrop({ end });
    if (positionEmpty(end)) onEnd();
  };

  const onChangePosition = (drop: Position) => {
    onChangeDrop({ drop });
    if (positionEmpty(drop)) onDrop();
  };

  const onChangeBidirectional = (bidirectional: boolean) =>
    onChangeDrop({ bidirectional });

  const heading = $(() => {
    const { start, end } = resolve(drop);
    if (positionEmpty(start) || positionEmpty(end)) return 0;
    return Math.round(toDegrees(groundBearing(start, end)));
  });

  const onChangeHeading = (heading: number) => {
    const { start, end } = resolve(drop);
    const distance = groundDistance(start, end);
    const newEnd = move(start, distance, toRadians(heading));
    onChangeDrop({ end: newEnd });
  };

  const onClickPreflight = async (event: MouseEvent) => {
    const simulation = event.shiftKey;
    await onPreflight({ simulation });
  };

  return (
    <Overlay visible={visible} class={style}>
      <div class="content">
        <div class="top">
          <img class="logo" src={logo} alt="Logo" />
          <Button icon title="Close" onClick={onClose}>
            <CloseIcon />
          </Button>
        </div>
        <H1>Edit mission</H1>
        <div class="grid">
          <Field label="Drop zone start">
            <PointInput
              point={$(() => resolve(drop).start)}
              onChangePoint={onChangeStart}
              onClick={onStart}
              onBlur={onDone}
            />
          </Field>
          <Field label="Drop zone end">
            <PointInput
              point={$(() => resolve(drop).end)}
              onChangePoint={onChangeEnd}
              onClick={onEnd}
              onBlur={onDone}
            />
          </Field>
          <Field label="Drop altitude (ft MSL)">
            <AltitudeInput
              altitude={$(() => resolve(drop).dropAltitude)}
              onChangeAltitude={onChangeDropAltitude}
            />
          </Field>
          <Field label="Drop position">
            <PointInput
              point={$(() => resolve(drop).drop)}
              onChangePoint={onChangePosition}
              onClick={onDrop}
              onBlur={onDone}
            />
          </Field>
          <Field label="Zone heading (°)">
            <DirectionInput
              position={$(() => resolve(drop).drop)}
              direction={heading}
              onChangeDirection={onChangeHeading}
            />
          </Field>
          <Field label="Bidirectional">
            <CheckboxInput
              value={$(() => resolve(drop).bidirectional)}
              onChange={onChangeBidirectional}
            />
          </Field>
          <Button onClick={onLoad}>
            <UploadIcon /> Load...
          </Button>
          <Button onClick={onSave}>
            <DownloadIcon /> Save...
          </Button>
          <Button onClick={onWeightBalance}>Weight & balance</Button>
          <LoadingButton disabled={incomplete} onClick={onSimulate}>
            <PlayIcon />
            Simulate
          </LoadingButton>
          <LoadingButton
            disabled={incomplete}
            primary
            onClick={onClickPreflight}
          >
            Preflight
          </LoadingButton>
        </div>
      </div>
    </Overlay>
  );
};
